/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Badge, Button, Collapse, Divider, message, Popconfirm, Skeleton, Tooltip } from 'antd';
import { Fragment, useState } from 'react';
import FloatingCard from '~components/FloatingCard';
import { activateSystem, deactivateSystem } from '~lib/netconnClient';
import constants from '~util/constants';
import handleAuthErr from '~util/handleAuthErr';
import logger from '~util/logger';
import CardTitle from './components/CardTitle';
import SystemAttribute from './components/SystemAttribute';
import AirlineCodeChangeModal from './modals/AirlineCodeChangeModal';
import { DeprovisionModal } from './modals/DeprovisionModal';
import SubnetGroupChangeModal from './modals/SubnetGroupChangeModal';
import SystemModelConversionModal from './modals/SystemModelConversionModal';

const { Panel } = Collapse;

const SystemDetailsPane = ({ config, division, gridArea, model, nai, updateConfig }) => {
  const [showAirlineCodeModal, setShowAirlineCodeModal] = useState(false);
  const [showSubnetGroupModal, setShowSubnetGroupModal] = useState(false);
  const [showModelConversionModal, setShowModelConversionModal] = useState(false);
  const [showDeprovisionModal, setShowDeprovisionModal] = useState(false);
  const [loading, setLoading] = useState({
    activating: false,
    deactivating: false,
    deprovisioning: false,
  });
  const { active, provision } = config;

  const modelName = model?.displayName || (config?.model ?? 'None');
  const airlineCode = config?.airlineCode ?? 'None';
  const subnetGroup = config?.subnetGroup ?? 'None';
  const numAvailableSubnetGroups = model?.subnetGroups?.length ?? 0;

  const handleActivateClick = () => {
    setLoading(prev => ({ ...prev, activating: true }));
    activateSystem(nai)
      .then(data => {
        if (data !== null) {
          updateConfig(data);
          setLoading(prev => ({ ...prev, activating: false }));
        }
      })
      .catch(
        handleAuthErr(err => {
          logger.error(err);
          message.error(constants.API_ERR_MESSAGE);
          setLoading(prev => ({ ...prev, activating: false }));
        })
      );
  };

  const handleDeactivateClick = () => {
    setLoading(prev => ({ ...prev, deactivating: true }));
    deactivateSystem(nai)
      .then(data => {
        if (data !== null) {
          updateConfig(data);
          setLoading(prev => ({ ...prev, deactivating: false }));
        }
      })
      .catch(
        handleAuthErr(err => {
          logger.err(err);
          message.error(constants.API_ERR_MESSAGE);
          setLoading(prev => ({ ...prev, deactivating: false }));
        })
      );
  };

  const toggleDeprovisionModal = () => setShowDeprovisionModal(!showDeprovisionModal);

  const cssRootContainer = css`
    border-color: ${active === undefined ? '#607d8b94' : !!active ? '#52c41b94' : '#d01c2594'};
    border-style: solid;
    border-width: 2px 0 0 0;
    display: flex;
    flex-direction: column;
    grid-area: ${gridArea};
    height: 100%;
    width: 100%;
  `;

  const cssHeader = css`
    align-items: center;
    display: flex;
    justify-content: space-between;
  `;

  const cssTitle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;

  const cssContentContainer = css`
    margin-left: 1rem;
  `;

  const deprovisionPanel = css`
    .ant-collapse-header {
      background-color: white;
    }
    .ant-collapse-content-active {
      text-align: center;
    }
  `;

  return (
    <FloatingCard css={cssRootContainer}>
      <div css={cssHeader}>
        <div css={cssTitle}>
          <Tooltip
            overlayStyle={{ textAlign: 'center', fontSize: '0.8rem' }}
            title={active === undefined ? 'Not Provisioned' : !!active ? 'Active' : 'Inactive'}
          >
            <Badge
              status={active === undefined ? 'default' : !!active ? 'success' : 'error'}
              title={active === undefined ? 'Not Provisioned' : !!active ? 'Active' : 'Inactive'}
            />
          </Tooltip>
          <CardTitle css={{ marginLeft: 0 }}>System Details</CardTitle>
        </div>
        {provision ? (
          <Tooltip
            overlayStyle={{ textAlign: 'center', fontSize: '0.8rem' }}
            title="BENS systems can not be manually activated or deactivated"
            trigger={airlineCode === constants.BENSENVILLE_CODE ? 'hover' : 'none'}
          >
            <Popconfirm
              disabled={airlineCode === constants.BENSENVILLE_CODE}
              okText="Confirm"
              okType="danger"
              onConfirm={!!active ? handleDeactivateClick : handleActivateClick}
              placement="bottomRight"
              title="Are you sure?"
            >
              <Button
                disabled={airlineCode === constants.BENSENVILLE_CODE}
                loading={!!active ? loading.deactivating : loading.activating}
                size="small"
                type="link"
              >
                {!!active ? 'Deactivate' : 'Activate'}
              </Button>
            </Popconfirm>
          </Tooltip>
        ) : provision !== undefined && !provision ? (
          <span>Deprovisioned</span>
        ) : null}
      </div>
      <Divider
        style={{
          margin: '0.6rem 0',
        }}
      />
      {Object.keys(config).length ? (
        <Fragment>
          <div css={cssContentContainer}>
            <SystemAttribute label={division === 'CA' ? 'Tail' : 'NAI'}>{nai}</SystemAttribute>
            {!!division && <SystemAttribute label="Division">{division}</SystemAttribute>}
            <SystemAttribute label="System Model">
              {modelName}
              {division === constants.CA && (
                <Tooltip
                  overlayStyle={{ textAlign: 'center', fontSize: '0.8rem' }}
                  title={
                    !!active
                      ? 'System must be inactive to change system model'
                      : 'Change System Model'
                  }
                >
                  <Button
                    icon="swap"
                    type="link"
                    disabled={!!active}
                    onClick={() => setShowModelConversionModal(true)}
                  />
                </Tooltip>
              )}
              {/* Checking explicitly for active === false (instead of !active) here as we don't want to render when active === null */}
            </SystemAttribute>
            {division !== constants.BA ? (
              <SystemAttribute label="Airline Code">
                {airlineCode}
                <Tooltip
                  overlayStyle={{ textAlign: 'center', fontSize: '0.8rem' }}
                  title={!!active ? 'Convert Fleet' : 'System must be active to convert fleet'}
                >
                  <Button
                    disabled={!active}
                    icon="edit"
                    onClick={() => setShowAirlineCodeModal(true)}
                    type="link"
                  />
                </Tooltip>
              </SystemAttribute>
            ) : (
              <SystemAttribute label="Subnet Group">
                {subnetGroup}
                {numAvailableSubnetGroups > 1 && (
                  <Tooltip
                    overlayStyle={{ textAlign: 'center', fontSize: '0.8rem' }}
                    title={!!active ? 'Change Subnet' : 'System must be active to change subnet'}
                  >
                    <Button
                      disabled={!active}
                      icon="edit"
                      onClick={() => setShowSubnetGroupModal(true)}
                      type="link"
                    />
                  </Tooltip>
                )}
              </SystemAttribute>
            )}
          </div>
          {provision && active && (
            <div>
              <Collapse css={deprovisionPanel}>
                <Panel header="Deprovision" key="1">
                  <h4>Deprovision this NAI</h4>
                  <p>
                    Once you deprovision a NAI, there is no going back. Please be certain you want
                    to do this.
                  </p>
                  <Button type="danger" onClick={toggleDeprovisionModal}>
                    Continue
                  </Button>
                </Panel>
              </Collapse>
            </div>
          )}
        </Fragment>
      ) : (
        <Skeleton paragraph={{ rows: 8 }} />
      )}
      {division === constants.CA && (
        <Fragment>
          <AirlineCodeChangeModal
            config={config}
            onClose={() => setShowAirlineCodeModal(false)}
            systemModel={modelName}
            updateConfig={updateConfig}
            visible={showAirlineCodeModal}
          />
          <SystemModelConversionModal
            config={config}
            onClose={() => setShowModelConversionModal(false)}
            updateConfig={updateConfig}
            visible={showModelConversionModal}
          />
        </Fragment>
      )}
      {division === constants.BA && (
        <SubnetGroupChangeModal
          config={config}
          onClose={() => setShowSubnetGroupModal(false)}
          systemSubnetGroups={model?.subnetGroups}
          updateConfig={updateConfig}
          visible={showSubnetGroupModal}
        />
      )}
      <DeprovisionModal
        isVisible={showDeprovisionModal}
        nai={nai}
        closeModal={toggleDeprovisionModal}
        confirmClick={updateConfig}
      />
    </FloatingCard>
  );
};

export default SystemDetailsPane;
